import { useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useStandardProductContext } from "app/providers/StandardProductProvider";
import {
  CategoryStandardProduct,
  StandardProductCategory,
} from "shared/api/middleware-api-client";
import { useStandardProductCategories } from "shared/api/hooks/useStandardProductCategories";

export const useStandardProduct = () => {
  const { store, actions, showStandardProducts } = useStandardProductContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const { standardProductCategories, categoriesLoading, isError } =
    useStandardProductCategories({ enabled: showStandardProducts });

  /** Utility: filter out accessory categories if the selected product is not an accessory */
  const filterCategories = useCallback(
    (
      selected: CategoryStandardProduct | undefined,
      categories: StandardProductCategory[],
    ): StandardProductCategory[] => {
      if (!selected) return categories;

      const selectedIsAccessory = selected.productType.code === "accessoire";
      if (selectedIsAccessory) return categories;

      return categories.filter((cat) => {
        const first = cat.product?.[0];
        return (
          !cat.disabled &&
          first &&
          first.standardProduct.productType.code !== "accessoire"
        );
      });
    },
    [],
  );

  /** Derived list used when we have a selected product + categories data */
  const filteredCategories = useMemo(() => {
    if (!standardProductCategories) return [];
    return filterCategories(
      store.selectedStandardProduct,
      standardProductCategories,
    );
  }, [
    filterCategories,
    store.selectedStandardProduct,
    standardProductCategories,
  ]);

  /** 1) Sync URL param from store (whenever selection changes) */
  useEffect(() => {
    if (!store.selectedStandardProduct) return;

    const current = searchParams.get("standard-product");
    const nextId = store.selectedStandardProduct.id;

    if (current !== nextId) {
      const next = new URLSearchParams(searchParams);
      next.set("standard-product", nextId);
      setSearchParams(next);
    }

    // Also keep the store's visible category list in sync with selection
    if (standardProductCategories) {
      actions.setStandardProductCategories(filteredCategories);
    }
  }, [
    store.selectedStandardProduct,
    searchParams,
    setSearchParams,
    actions,
    standardProductCategories,
    filteredCategories,
  ]);

  /** 2) Initialize store from URL param (runs once when categories arrive) */
  useEffect(() => {
    if (categoriesLoading || isError || !standardProductCategories) return;
    if (store.selectedStandardProduct) {
      // selection already there; ensure categories list set at least once
      actions.setStandardProductCategories(filteredCategories);
      return;
    }

    const idFromUrl = searchParams.get("standard-product");
    if (!idFromUrl) {
      // no deep-link → just expose full list initially
      actions.setStandardProductCategories(standardProductCategories);
      return;
    }

    // Find the product by id across categories
    let foundCategory: StandardProductCategory | undefined;
    let foundProduct: CategoryStandardProduct | undefined;

    outer: for (const cat of standardProductCategories) {
      for (const p of cat.product ?? []) {
        if (p.standardProduct.id === idFromUrl) {
          foundCategory = cat;
          foundProduct = p.standardProduct;
          break outer;
        }
      }
    }

    if (foundCategory && foundProduct) {
      // clone before mutating amount to avoid touching cached objects
      const selectedClone: CategoryStandardProduct = {
        ...foundProduct,
        amount: 1,
      };

      actions.selectStandardProductCategory(foundCategory);
      actions.selectStandardProduct(selectedClone);

      const nextCats = filterCategories(
        selectedClone,
        standardProductCategories,
      );
      actions.setStandardProductCategories(nextCats);
    } else {
      // fallback: unknown id → expose full list
      actions.setStandardProductCategories(standardProductCategories);
    }
  }, [
    categoriesLoading,
    isError,
    standardProductCategories,
    searchParams,
    store.selectedStandardProduct,
    actions,
    filterCategories,
    filteredCategories,
  ]);

  return { store, actions };
};
